import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, AlertTriangle, Check, Info } from 'lucide-react'
import { nsApi } from '../services/api'
import NsRecordTypeCombobox from './NsRecordTypeCombobox'
import { parseMissingFieldLabels, resolveFieldsByLabel } from '../utils/nsRequiredProbe'

// ─── NsLineLinkWizard ──────────────────────────────────────────────────────
//
// Replaces the old "type in a raw field id and hope" step of the "Add a
// NetSuite record type" wizard with a proof-based decision tree:
//
//   1. detect-shape tells us whether the record has its own native line
//      sublist (Branch 1, e.g. Purchase Order) — if so, nothing else to ask.
//   2. Otherwise: does the admin have a real example of this record already
//      in NetSuite?
//        Yes -> Branch 2: pick/paste an example id, scan real data for
//               whichever custom record type + field actually references it.
//        No  -> Branch 3: guided-create one throwaway header + line, then run
//               the identical scan against the header we just made.
//   3. Whichever branch found a match, it must pass a real create-read-delete
//      verification before the wizard reports itself "ready".
//
// Zero hardcoded record/field names — everything shown comes from detect-shape
// / scan-link / the record type's own live field catalog.
//
// Props:
//   headerRecordType: { record_type_key, display_label } — required. Pass a
//     `key={headerRecordType.record_type_key}` on this component from the
//     parent so picking a different header type remounts it with fresh state.
//   disabled: bool — true while the parent is submitting the final "Add
//     record type" call; freezes further interaction.
//   onStateChange({ ready, lineConfig }): called whenever readiness changes.
//     lineConfig is `null` for Branch 1 (no line items) or whenever not yet
//     resolved; once verified it is { lineRecordTypeKey, lineLinkField }.

const MANDATORY_FIELDS_EMPTY_NOTE = 'This record type has no mandatory fields — it can be created with no values at all.'

function fieldToPayloadValue(field, rawValue) {
  const value = (rawValue ?? '').toString().trim()
  if (field.is_reference) return value ? { id: value } : undefined
  if (field.type === 'boolean') return rawValue === true || rawValue === 'true'
  if (field.type === 'number' || field.type === 'integer') {
    if (value === '') return undefined
    const n = Number(value)
    return Number.isNaN(n) ? value : n
  }
  return value || undefined
}

function buildPayload(fields, values) {
  const payload = {}
  fields.forEach(f => {
    const v = fieldToPayloadValue(f, values[f.key])
    if (v !== undefined) payload[f.key] = v
  })
  return payload
}

// NetSuite's own schema metadata is not a reliable source of truth for what
// it actually requires at write time — confirmed live that a record type
// reporting zero mandatory fields (nullable: true on every field, including
// its own name field) still rejects an empty create with
// "Error while accessing a resource. Please enter value(s) for: Name." A
// schema-only guided form has no way to recover from this: there is simply
// no input rendered for a field the schema never flagged as required, so a
// retry fails identically forever. Parsing NetSuite's own rejection message
// for the field label(s) it names, and adding those to the form, turns
// NetSuite's live validation into the discovery signal instead of the
// schema's (possibly incomplete) declaration — the same "proof, not a
// guess" principle the rest of this wizard already follows.
//
// The parser itself now lives in utils/nsRequiredProbe.js, shared with the
// "Test This Configuration" probe — both read the same rejection strings
// from the same account, so two private copies would eventually disagree.

// Shared by MandatoryFieldsForm (rendering) and the two submit handlers
// (payload building), so the fields the admin can see and fill are always
// exactly the fields that get posted — no field visible in the form that
// silently gets dropped from the request.
//
// For the line form only (referenceHeaderId set): none of NetSuite's own
// signals — schema `mandatory`, and the create-time rejection message — ever
// name the field that actually links this line back to its header. Both
// signals are about what NetSuite refuses to save without, not about which
// field represents a relationship the record's own data model may leave
// fully optional. Without exposing every reference-type field here, this
// example line has no way to end up linked to the header at all, so the
// scan that follows has nothing real to find — confirmed live: a line
// created from only the mandatory/discovered fields came back from NetSuite
// with no reference field set whatsoever. The admin — not this wizard — is
// the one who knows which reference means "belongs to this header", per the
// wizard's own principle.
function computeFormFields(catalog, extraFields, referenceHeaderId) {
  const declared = (catalog || []).filter(f => f.mandatory)
  const declaredKeys = new Set(declared.map(f => f.key))
  const extra = (extraFields || []).filter(f => !declaredKeys.has(f.key))
  const seen = new Set([...declaredKeys, ...extra.map(f => f.key)])
  const referenceFields = referenceHeaderId
    ? (catalog || []).filter(f => f.is_reference && !seen.has(f.key))
    : []
  return [...declared, ...extra, ...referenceFields]
}

// A minimal-fields form for a record type's mandatory fields — deliberately
// plain text/number inputs, not the full runtime request-view pickers. Used
// for both the Branch 3 header and line guided-create steps.
function MandatoryFieldsForm({ title, catalog, catalogLoading, catalogError, values, onChange, onSubmit, submitting, submitError, submitLabel, extraFields = [], referenceHeaderId = null }) {
  const mandatory = useMemo(
    () => computeFormFields(catalog, extraFields, referenceHeaderId),
    [catalog, extraFields, referenceHeaderId]
  )
  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">{title}</p>
      {referenceHeaderId && (
        <div className="flex items-start gap-2 rounded-lg bg-navy-50 dark:bg-navy-900/20 border border-navy-200 dark:border-navy-800 px-3 py-2.5">
          <Info className="h-3.5 w-3.5 text-navy-500 mt-0.5 flex-shrink-0" />
          <p className="text-[12px] text-navy-700 dark:text-navy-300">
            This example line needs to actually reference header record <strong>#{referenceHeaderId}</strong>. Enter <strong>{referenceHeaderId}</strong> into whichever field below represents "belongs to this header" for this record type — only you know which one that is.
          </p>
        </div>
      )}
      {catalogLoading && (
        <div className="flex items-center gap-2 text-[12px] text-gray-500 dark:text-gray-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading field list from NetSuite…
        </div>
      )}
      {catalogError && !catalogLoading && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2.5">
          <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-[12px] text-red-600 dark:text-red-400">{catalogError}</p>
        </div>
      )}
      {!catalogLoading && !catalogError && (
        <>
          {mandatory.length === 0 ? (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 italic">{MANDATORY_FIELDS_EMPTY_NOTE}</p>
          ) : (
            <div className="space-y-2.5">
              {mandatory.map(f => (
                <div key={f.key}>
                  <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                    {f.label} {f.is_reference && <span className="text-gray-400 font-normal">(enter its internal id)</span>}
                  </label>
                  <input
                    type={f.type === 'number' || f.type === 'integer' ? 'number' : 'text'}
                    autoComplete="off"
                    value={values[f.key] ?? ''}
                    onChange={e => onChange(f.key, e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-[13px] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500"
                  />
                </div>
              ))}
            </div>
          )}
          {submitError && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2.5">
              <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-[12px] text-red-600 dark:text-red-400">{submitError}</p>
            </div>
          )}
          <button
            type="button"
            disabled={submitting}
            onClick={onSubmit}
            className="px-3 py-1.5 text-[12px] font-medium bg-navy-600 hover:bg-navy-700 text-white rounded-lg disabled:opacity-40 flex items-center gap-1.5"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {submitLabel}
          </button>
        </>
      )}
    </div>
  )
}

// Renders scan-link's matches — always the human-readable line_preview, never
// raw field ids in the primary display. Shared by Branch 2 and Branch 3.
function MatchList({ matches, selectedIndex, onSelect }) {
  return (
    <div className="space-y-2">
      {matches.map((m, idx) => {
        const selected = idx === selectedIndex
        return (
          <button
            key={`${m.line_record_type_key}-${m.field_key}-${idx}`}
            type="button"
            onClick={() => onSelect(idx)}
            className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
              selected
                ? 'border-navy-500 bg-navy-50 dark:bg-navy-900/30'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <div className="flex items-start gap-2">
              <span className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center ${selected ? 'border-navy-500 bg-navy-500' : 'border-gray-300 dark:border-gray-600'}`}>
                {selected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">{m.display_label}</p>
                <p className="text-[10px] text-gray-400 font-mono truncate">{m.field_key}</p>
                {m.line_preview?.length > 0 && (
                  <dl className="mt-1.5 space-y-0.5">
                    {m.line_preview.map((row, i) => (
                      <div key={i} className="flex gap-1.5 text-[11px]">
                        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{row.label}:</dt>
                        <dd className="text-gray-800 dark:text-gray-200 truncate">{String(row.value)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}

export default function NsLineLinkWizard({ headerRecordType, disabled = false, onStateChange }) {
  const headerKey = headerRecordType?.record_type_key

  // 'shape-loading' | 'shape-error' | 'branch1' | 'ask-example' |
  // 'example-picker' | 'scanning' | 'no-matches' | 'matches' |
  // 'branch3-header' | 'branch3-line-pick' | 'branch3-line-form' |
  // 'branch3-cleanup' | 'verifying' | 'verify-error' | 'verified'
  const [phase, setPhase] = useState('shape-loading')
  const [shapeError, setShapeError] = useState('')
  const [isBranch3, setIsBranch3] = useState(false)

  // Branch 2: example picker
  const [listItems, setListItems] = useState([])
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState('')
  const [pickedId, setPickedId] = useState('')
  const [pastedId, setPastedId] = useState('')
  const [confirmingExample, setConfirmingExample] = useState(false)
  const [exampleError, setExampleError] = useState('')

  // Shared: the id being scanned + the header record's own raw fields (used
  // later as verify-link's header_fields), and the scan results.
  const [exampleId, setExampleId] = useState(null)
  const [headerFieldsForVerify, setHeaderFieldsForVerify] = useState({})
  const [matches, setMatches] = useState([])
  const [scanError, setScanError] = useState('')
  const [selectedMatchIndex, setSelectedMatchIndex] = useState(null)

  // Branch 3: guided create
  const [headerCatalog, setHeaderCatalog] = useState([])
  const [headerCatalogLoading, setHeaderCatalogLoading] = useState(false)
  const [headerCatalogError, setHeaderCatalogError] = useState('')
  const [headerFormValues, setHeaderFormValues] = useState({})
  const [headerCreating, setHeaderCreating] = useState(false)
  const [headerCreateError, setHeaderCreateError] = useState('')
  const [createdHeaderId, setCreatedHeaderId] = useState(null)
  // Fields NetSuite's own create rejection named as required, on top of
  // whatever the schema declared mandatory — see parseMissingFieldLabels.
  const [headerExtraFields, setHeaderExtraFields] = useState([])

  const [lineRecordType, setLineRecordType] = useState(null)
  const [lineCatalog, setLineCatalog] = useState([])
  const [lineCatalogLoading, setLineCatalogLoading] = useState(false)
  const [lineCatalogError, setLineCatalogError] = useState('')
  const [lineFormValues, setLineFormValues] = useState({})
  const [lineCreating, setLineCreating] = useState(false)
  const [lineCreateError, setLineCreateError] = useState('')
  const [createdLineId, setCreatedLineId] = useState(null)
  const [lineExtraFields, setLineExtraFields] = useState([])

  const [cleanupLoading, setCleanupLoading] = useState(false)
  const [cleanupWarning, setCleanupWarning] = useState('')

  // Verification
  const [verifyError, setVerifyError] = useState('')
  const [verifiedConfig, setVerifiedConfig] = useState(null)
  // Branch 1's discovered native line-items sublist — must reach the parent
  // via onStateChange's lineConfig, or "Add record type" persists this
  // record type as having no lines at all. Confirmed live: Purchase Order's
  // config ended up with has_line_items=false/post_mechanics='header_only'
  // because this was previously always reported as null for Branch 1.
  const [nativeSublistKey, setNativeSublistKey] = useState(null)

  // ── Step 1: detect-shape, the moment a header record type is picked ──────
  useEffect(() => {
    if (!headerKey) return
    let cancelled = false
    setPhase('shape-loading')
    setShapeError('')
    nsApi.detectRecordShape(headerKey)
      .then(res => {
        if (cancelled) return
        if (res.data?.is_standard) {
          setNativeSublistKey(res.data?.sublist_key || null)
          setPhase('branch1')
        } else {
          setPhase('ask-example')
        }
      })
      .catch(err => {
        if (cancelled) return
        setShapeError(err?.response?.data?.error || 'Could not determine this record type\'s structure.')
        setPhase('shape-error')
      })
    return () => { cancelled = true }
  }, [headerKey])

  // Report readiness up to the parent.
  useEffect(() => {
    if (phase === 'branch1') {
      onStateChange?.({
        ready: true,
        lineConfig: nativeSublistKey ? { isEmbedded: true, lineContainerKey: nativeSublistKey } : null,
      })
    } else if (phase === 'verified' && verifiedConfig) {
      onStateChange?.({ ready: true, lineConfig: verifiedConfig })
    } else {
      onStateChange?.({ ready: false, lineConfig: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, verifiedConfig, nativeSublistKey])

  // ── Branch 2: load the recent-records browse list on entering the picker ─
  useEffect(() => {
    if (phase !== 'example-picker' || !headerKey) return
    let cancelled = false
    setListLoading(true)
    setListError('')
    nsApi.listRecords(headerKey, 25)
      .then(res => {
        if (cancelled) return
        setListItems(res.data?.items || [])
      })
      .catch(err => {
        if (cancelled) return
        setListItems([])
        setListError(err?.response?.data?.error || 'Could not load recent records.')
      })
      .finally(() => { if (!cancelled) setListLoading(false) })
    return () => { cancelled = true }
  }, [phase, headerKey])

  // ── Branch 3: load the header record type's own field catalog ───────────
  useEffect(() => {
    if (phase !== 'branch3-header' || !headerKey) return
    let cancelled = false
    setHeaderCatalogLoading(true)
    setHeaderCatalogError('')
    nsApi.getRecordTypeFieldCatalog(headerKey)
      .then(res => {
        if (cancelled) return
        setHeaderCatalog(res.data?.available_fields || [])
      })
      .catch(err => {
        if (cancelled) return
        setHeaderCatalog([])
        setHeaderCatalogError(err?.response?.data?.error || 'Could not load this record type\'s fields.')
      })
      .finally(() => { if (!cancelled) setHeaderCatalogLoading(false) })
    return () => { cancelled = true }
  }, [phase, headerKey])

  // ── Branch 3: load the chosen line record type's field catalog ──────────
  useEffect(() => {
    if (phase !== 'branch3-line-form' || !lineRecordType) return
    let cancelled = false
    setLineCatalogLoading(true)
    setLineCatalogError('')
    nsApi.getRecordTypeFieldCatalog(lineRecordType.record_type_key)
      .then(res => {
        if (cancelled) return
        setLineCatalog(res.data?.available_fields || [])
      })
      .catch(err => {
        if (cancelled) return
        setLineCatalog([])
        setLineCatalogError(err?.response?.data?.error || 'Could not load this record type\'s fields.')
      })
      .finally(() => { if (!cancelled) setLineCatalogLoading(false) })
    return () => { cancelled = true }
  }, [phase, lineRecordType])

  const runScan = useCallback((id, headerFields) => {
    setExampleId(id)
    setHeaderFieldsForVerify(headerFields || {})
    setSelectedMatchIndex(null)
    setScanError('')
    setPhase('scanning')
    nsApi.scanForLineLink(id, headerKey)
      .then(res => {
        const found = res.data?.matches || []
        setMatches(found)
        setPhase(found.length > 0 ? 'matches' : 'no-matches')
      })
      .catch(err => {
        setMatches([])
        setScanError(err?.response?.data?.error || 'Could not scan NetSuite for a matching line record.')
        setPhase('no-matches')
      })
  }, [headerKey])

  // ── Branch 2: confirm a picked-or-pasted example id, then scan ──────────
  const confirmExample = useCallback(() => {
    const id = (pastedId.trim() || pickedId || '').toString().trim()
    if (!id) return
    setConfirmingExample(true)
    setExampleError('')
    nsApi.getRecordById(headerKey, id)
      .then(res => {
        const record = res.data?.record
        if (!record) {
          setExampleError(`Record #${id} not found.`)
          return
        }
        runScan(id, record)
      })
      .catch(err => {
        setExampleError(err?.response?.data?.error || `Record #${id} not found.`)
      })
      .finally(() => setConfirmingExample(false))
  }, [pastedId, pickedId, headerKey, runScan])

  // Shared failure handling for both guided-create calls: the backend
  // returns a non-2xx status on a failed create (guided_create_ns_record
  // returns 422), so axios always routes this through .catch(), never the
  // .then() success branch — discovery has to run from the caught error,
  // not from a `{success: false}` body that never actually arrives here.
  const handleCreateFailure = (err, catalog, setExtraFields, setError, fallback) => {
    const message = err?.response?.data?.error || fallback
    setError(message)
    const discovered = resolveFieldsByLabel(catalog, parseMissingFieldLabels(message))
    if (discovered.length) {
      setExtraFields(prev => {
        const keys = new Set(prev.map(f => f.key))
        return [...prev, ...discovered.filter(f => !keys.has(f.key))]
      })
    }
  }

  // ── Branch 3: create the throwaway header ────────────────────────────────
  const submitHeaderForm = useCallback(() => {
    setHeaderCreating(true)
    setHeaderCreateError('')
    const fields = computeFormFields(headerCatalog, headerExtraFields, null)
    const payload = buildPayload(fields, headerFormValues)
    nsApi.createGuidedRecord(headerKey, payload)
      .then(res => {
        const data = res.data || {}
        if (!data.success) {
          handleCreateFailure({ response: { data } }, headerCatalog, setHeaderExtraFields, setHeaderCreateError, 'Could not create the example header record.')
          return
        }
        setCreatedHeaderId(data.id)
        setHeaderFieldsForVerify(payload)
        setIsBranch3(true)
        setPhase('branch3-line-pick')
      })
      .catch(err => {
        handleCreateFailure(err, headerCatalog, setHeaderExtraFields, setHeaderCreateError, 'Could not create the example header record.')
      })
      .finally(() => setHeaderCreating(false))
  }, [headerCatalog, headerExtraFields, headerFormValues, headerKey])

  // ── Branch 3: create the throwaway line, then scan from the new header ──
  const submitLineForm = useCallback(() => {
    setLineCreating(true)
    setLineCreateError('')
    const fields = computeFormFields(lineCatalog, lineExtraFields, createdHeaderId)
    const payload = buildPayload(fields, lineFormValues)
    nsApi.createGuidedRecord(lineRecordType.record_type_key, payload)
      .then(res => {
        const data = res.data || {}
        if (!data.success) {
          handleCreateFailure({ response: { data } }, lineCatalog, setLineExtraFields, setLineCreateError, 'Could not create the example line record.')
          return
        }
        setCreatedLineId(data.id)
        runScan(createdHeaderId, headerFieldsForVerify)
      })
      .catch(err => {
        handleCreateFailure(err, lineCatalog, setLineExtraFields, setLineCreateError, 'Could not create the example line record.')
      })
      .finally(() => setLineCreating(false))
  }, [lineCatalog, lineExtraFields, lineFormValues, lineRecordType, createdHeaderId, headerFieldsForVerify, runScan])

  const runVerify = useCallback(() => {
    const match = matches[selectedMatchIndex]
    if (!match) return
    setPhase('verifying')
    setVerifyError('')
    nsApi.verifyLineLink({
      headerRecordTypeKey: headerKey,
      lineRecordTypeKey: match.line_record_type_key,
      lineLinkField: match.field_key,
      headerFields: headerFieldsForVerify,
      lineFields: match.raw_fields,
    })
      .then(res => {
        const data = res.data || {}
        if (data.verified) {
          setVerifiedConfig({ lineRecordTypeKey: match.line_record_type_key, lineLinkField: match.field_key })
          setPhase('verified')
        } else {
          setVerifyError(data.error || 'Verification failed.')
          setPhase('verify-error')
        }
      })
      .catch(err => {
        setVerifyError(err?.response?.data?.error || 'Verification failed — NetSuite may be unreachable.')
        setPhase('verify-error')
      })
  }, [matches, selectedMatchIndex, headerKey, headerFieldsForVerify])

  const confirmMatch = useCallback(() => {
    if (selectedMatchIndex === null) return
    if (isBranch3 && (createdHeaderId || createdLineId)) {
      setPhase('branch3-cleanup')
    } else {
      runVerify()
    }
  }, [selectedMatchIndex, isBranch3, createdHeaderId, createdLineId, runVerify])

  const cleanupExamples = useCallback((shouldDelete) => {
    if (!shouldDelete) {
      runVerify()
      return
    }
    setCleanupLoading(true)
    setCleanupWarning('')
    Promise.allSettled([
      createdLineId ? nsApi.deleteRecordById(lineRecordType.record_type_key, createdLineId) : Promise.resolve(),
      createdHeaderId ? nsApi.deleteRecordById(headerKey, createdHeaderId) : Promise.resolve(),
    ]).then(results => {
      const failed = results.some(r => r.status === 'rejected' || (r.value && r.value.data && r.value.data.success === false))
      if (failed) setCleanupWarning('Could not delete one or both example records — you may want to remove them manually in NetSuite.')
    }).finally(() => {
      setCleanupLoading(false)
      runVerify()
    })
  }, [createdLineId, createdHeaderId, lineRecordType, headerKey, runVerify])

  const retryDifferentExample = useCallback(() => {
    setPickedId('')
    setPastedId('')
    setExampleError('')
    setMatches([])
    setScanError('')
    setSelectedMatchIndex(null)
    setPhase('example-picker')
  }, [])

  const switchToGuidedSetup = useCallback(() => {
    setMatches([])
    setScanError('')
    setSelectedMatchIndex(null)
    setIsBranch3(true)
    setHeaderFormValues({})
    setPhase('branch3-header')
  }, [])

  if (!headerRecordType) return null

  return (
    <div className="space-y-3 pt-1 border-t border-gray-100 dark:border-gray-800">
      {phase === 'shape-loading' && (
        <div className="flex items-center gap-2 text-[12px] text-gray-500 dark:text-gray-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking {headerRecordType.display_label}'s structure in NetSuite…
        </div>
      )}

      {phase === 'shape-error' && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2.5">
          <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-[12px] text-red-600 dark:text-red-400">{shapeError}</p>
        </div>
      )}

      {phase === 'branch1' && (
        <div className="flex items-start gap-2 rounded-lg bg-navy-50 dark:bg-navy-900/20 border border-navy-200 dark:border-navy-800 px-3 py-2.5">
          <Info className="h-3.5 w-3.5 text-navy-500 mt-0.5 flex-shrink-0" />
          <p className="text-[12px] text-navy-700 dark:text-navy-300">
            This record type has its own line items built in — nothing else to configure.
          </p>
        </div>
      )}

      {phase === 'ask-example' && (
        <div>
          <label className="block text-xs font-semibold text-gray-800 dark:text-gray-200 mb-2">
            Do you have a real example of {headerRecordType.display_label} already in NetSuite?
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() => setPhase('example-picker')}
              className="text-left px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-[12px] text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
            >
              <span className="block font-medium">Yes</span>
              <span className="block text-[11px] opacity-80">Pick or paste an existing record's id</span>
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={switchToGuidedSetup}
              className="text-left px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-[12px] text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
            >
              <span className="block font-medium">No / it's brand new</span>
              <span className="block text-[11px] opacity-80">Guided setup — create one to discover the link</span>
            </button>
          </div>
        </div>
      )}

      {phase === 'example-picker' && (
        <div className="space-y-3">
          <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">
            Pick a recent {headerRecordType.display_label} record, or paste its id
          </p>
          {listLoading && (
            <div className="flex items-center gap-2 text-[12px] text-gray-500 dark:text-gray-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading recent records…
            </div>
          )}
          {listError && <p className="text-[12px] text-red-500">{listError}</p>}
          {!listLoading && !listError && listItems.length > 0 && (
            <div className="max-h-32 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
              {listItems.map(item => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => { setPickedId(String(item.id)); setPastedId('') }}
                  className={`w-full text-left px-3 py-1.5 text-[12px] font-mono ${
                    pickedId === String(item.id)
                      ? 'bg-navy-50 dark:bg-navy-900/30 text-navy-700 dark:text-navy-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  #{item.id}
                </button>
              ))}
            </div>
          )}
          {!listLoading && !listError && listItems.length === 0 && (
            <p className="text-[11px] text-gray-400 italic">No recent records found — you can still paste an id below.</p>
          )}
          <div>
            <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">Or paste an id directly</label>
            <input
              type="text"
              autoComplete="off"
              value={pastedId}
              onChange={e => { setPastedId(e.target.value); setPickedId('') }}
              placeholder="e.g. 12345"
              className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-[13px] text-gray-900 dark:text-gray-100 focus:outline-none"
            />
          </div>
          {exampleError && <p className="text-[11px] text-red-500">{exampleError}</p>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={disabled || confirmingExample || !(pastedId.trim() || pickedId)}
              onClick={confirmExample}
              className="px-3 py-1.5 text-[12px] font-medium bg-navy-600 hover:bg-navy-700 text-white rounded-lg disabled:opacity-40 flex items-center gap-1.5"
            >
              {confirmingExample && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Use this record
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={switchToGuidedSetup}
              className="text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Switch to guided setup instead
            </button>
          </div>
        </div>
      )}

      {phase === 'scanning' && (
        <div className="flex items-start gap-2 rounded-lg bg-navy-50 dark:bg-navy-900/20 border border-navy-200 dark:border-navy-800 px-3 py-2.5">
          <Loader2 className="h-3.5 w-3.5 text-navy-500 animate-spin mt-0.5 flex-shrink-0" />
          <p className="text-[12px] text-navy-700 dark:text-navy-300">
            Scanning the account's records for whichever one actually references example #{exampleId}…
          </p>
        </div>
      )}

      {phase === 'no-matches' && (
        <div className="space-y-2.5">
          <div className="flex items-start gap-2 rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 px-3 py-2.5">
            <AlertTriangle className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
            <p className="text-[12px] text-gray-600 dark:text-gray-300">
              {scanError || `No record type in this account has any data referencing example #${exampleId}.`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" disabled={disabled} onClick={retryDifferentExample} className="text-[11px] font-medium text-navy-600 dark:text-navy-400 hover:text-navy-700 dark:hover:text-navy-300">
              Try a different example
            </button>
            <button type="button" disabled={disabled} onClick={switchToGuidedSetup} className="text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
              Switch to guided setup
            </button>
          </div>
        </div>
      )}

      {phase === 'matches' && (
        <div className="space-y-3">
          <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">
            {matches.length === 1 ? 'Found one record type that references this example' : `Found ${matches.length} record types that reference this example`}
          </p>
          <MatchList matches={matches} selectedIndex={selectedMatchIndex} onSelect={setSelectedMatchIndex} />
          <button
            type="button"
            disabled={disabled || selectedMatchIndex === null}
            onClick={confirmMatch}
            className="px-3 py-1.5 text-[12px] font-medium bg-navy-600 hover:bg-navy-700 text-white rounded-lg disabled:opacity-40 flex items-center gap-1.5"
          >
            <Check className="h-3.5 w-3.5" /> Confirm this is correct
          </button>
        </div>
      )}

      {phase === 'branch3-header' && (
        <MandatoryFieldsForm
          title={`Create one example ${headerRecordType.display_label} record`}
          catalog={headerCatalog}
          catalogLoading={headerCatalogLoading}
          catalogError={headerCatalogError}
          values={headerFormValues}
          onChange={(key, value) => setHeaderFormValues(v => ({ ...v, [key]: value }))}
          onSubmit={submitHeaderForm}
          submitting={headerCreating}
          submitError={headerCreateError}
          submitLabel="Create example header"
          extraFields={headerExtraFields}
        />
      )}

      {phase === 'branch3-line-pick' && (
        <div className="space-y-2.5">
          <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">Which record holds the line items?</p>
          <NsRecordTypeCombobox
            value={lineRecordType}
            onChange={setLineRecordType}
            excludeKey={headerKey}
            placeholder="Search for the line record…"
          />
          <button
            type="button"
            disabled={disabled || !lineRecordType}
            onClick={() => { setLineFormValues({}); setLineExtraFields([]); setPhase('branch3-line-form') }}
            className="px-3 py-1.5 text-[12px] font-medium bg-navy-600 hover:bg-navy-700 text-white rounded-lg disabled:opacity-40"
          >
            Continue
          </button>
        </div>
      )}

      {phase === 'branch3-line-form' && (
        <MandatoryFieldsForm
          title={`Create one example ${lineRecordType?.display_label} line`}
          catalog={lineCatalog}
          catalogLoading={lineCatalogLoading}
          catalogError={lineCatalogError}
          values={lineFormValues}
          onChange={(key, value) => setLineFormValues(v => ({ ...v, [key]: value }))}
          onSubmit={submitLineForm}
          submitting={lineCreating}
          submitError={lineCreateError}
          submitLabel="Create example line"
          extraFields={lineExtraFields}
          referenceHeaderId={createdHeaderId}
        />
      )}

      {phase === 'branch3-cleanup' && (
        <div className="space-y-2.5">
          <p className="text-[12px] text-gray-600 dark:text-gray-300">
            Delete the example records created for this discovery (#{createdHeaderId} and #{createdLineId}), or keep them?
          </p>
          {cleanupWarning && <p className="text-[11px] text-amber-600 dark:text-amber-400">{cleanupWarning}</p>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={disabled || cleanupLoading}
              onClick={() => cleanupExamples(true)}
              className="px-3 py-1.5 text-[12px] font-medium bg-navy-600 hover:bg-navy-700 text-white rounded-lg disabled:opacity-40 flex items-center gap-1.5"
            >
              {cleanupLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Delete these example records now
            </button>
            <button
              type="button"
              disabled={disabled || cleanupLoading}
              onClick={() => cleanupExamples(false)}
              className="px-3 py-1.5 text-[12px] font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Keep them
            </button>
          </div>
        </div>
      )}

      {phase === 'verifying' && (
        <div className="flex items-start gap-2 rounded-lg bg-navy-50 dark:bg-navy-900/20 border border-navy-200 dark:border-navy-800 px-3 py-2.5">
          <Loader2 className="h-3.5 w-3.5 text-navy-500 animate-spin mt-0.5 flex-shrink-0" />
          <p className="text-[12px] text-navy-700 dark:text-navy-300">
            Verifying — creating a real throwaway header and line in NetSuite to prove this link resolves, then deleting them. This can take a few seconds.
          </p>
        </div>
      )}

      {phase === 'verify-error' && (
        <div className="space-y-2.5">
          <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2.5">
            <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-[12px] text-red-600 dark:text-red-400">{verifyError}</p>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" disabled={disabled} onClick={runVerify} className="text-[11px] font-medium text-navy-600 dark:text-navy-400 hover:text-navy-700 dark:hover:text-navy-300">
              Try again
            </button>
            <button type="button" disabled={disabled} onClick={() => setPhase('matches')} className="text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
              Back to matches
            </button>
          </div>
        </div>
      )}

      {phase === 'verified' && (
        <div className="flex items-start gap-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-2.5">
          <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <p className="text-[12px] text-green-700 dark:text-green-300">
            Verified — {verifiedConfig?.lineRecordTypeKey} lines link back via {verifiedConfig?.lineLinkField}. Ready to add this record type.
          </p>
        </div>
      )}
    </div>
  )
}
